import * as React from 'react';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import courseCover from 'src/app/images/placeholders/show-course.jpg';

const CourseBanner = React.forwardRef(({ className, backgroundUrl, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'c-course-banner',
        // Estilos base
        'w-full',
        'overflow-hidden rounded-xl bg-primary-lightest bg-cover bg-center',
        'p-3 md:p-[2vw]',
        'relative flex flex-col text-white',
        className
      )}
      {...props}
    >
      <CourseBannerImg src={backgroundUrl} />
      <div
        className={cn('absolute inset-0 z-10', 'bg-black/40')}
        aria-hidden="true"
      />
      <div className="relative z-20">{children}</div>
    </div>
  );
});
CourseBanner.displayName = 'CourseBanner';

const CourseBannerContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-banner-content',
      // Estilos base
      'flex flex-wrap-reverse items-end',
      'gap-4 md:gap-x-32',
      className
    )}
    {...props}
  />
));
CourseBannerContent.displayName = 'CourseBannerContent';

const CourseBannerContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-banner-container',
      // Estilos base
      'w-[220px] max-w-full flex-grow',
      className
    )}
    {...props}
  />
));
CourseBannerContainer.displayName = 'CourseBannerContainer';

const CourseBannerHat = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-course-banner-hat',
      // Estilos base
      'mb-0 text-xs font-light',
      className
    )}
    {...props}
  />
));
CourseBannerHat.displayName = 'CourseBannerHat';

const CourseBannerTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      'c-course-banner-title',
      // Estilos base
      'relative z-20',
      'mb-2 text-3xl font-bold leading-snug text-white',
      'break-words',
      className
    )}
    {...props}
  />
));
CourseBannerTitle.displayName = 'CourseBannerTitle';

const CourseBannerDescription = React.forwardRef(({ className, asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : 'p';

  return (
    <Comp
      ref={ref}
      className={cn(
        'c-course-banner-description',
        // Estilos base
        'relative z-20',
        'max-w-[300px] font-light',
        className
      )}
      {...props}
    />
  );
});
CourseBannerDescription.displayName = 'CourseBannerDescription';

const CourseBannerBy = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-course-banner-by',
      // Estilos base
      'mb-0 text-right text-xs font-light',
      className
    )}
    {...props}
  />
));
CourseBannerBy.displayName = 'CourseBannerBy';

const CourseBannerCTA = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-banner-cta',
      // Estilos base
      'relative z-20',
      'mt-[4em] flex flex-wrap items-center gap-5',
      className
    )}
    {...props}
  />
));
CourseBannerCTA.displayName = 'CourseBannerCTA';

const CourseBannerImg = React.forwardRef(({ className, src, alt, ...props }, ref) => {
  const [imgSrc, setImgSrc] = React.useState(src || courseCover);

  const handleError = () => {
    if (imgSrc !== courseCover) {
      setImgSrc(courseCover);
    }
  };

  React.useEffect(() => {
    setImgSrc(src || courseCover);
  }, [src]);

  return (
    <img
      ref={ref}
      src={imgSrc}
      onError={handleError}
      alt={alt || 'imagem do curso'}
      className={cn(
        'c-course-banner-img',
        // Estilos base
        'absolute inset-0 z-0 h-full w-full object-cover',
        className
      )}
      {...props}
    />
  );
});
CourseBannerImg.displayName = 'CourseBannerImg';

export { CourseBanner, CourseBannerContent, CourseBannerContainer, CourseBannerHat, CourseBannerTitle, CourseBannerDescription, CourseBannerBy, CourseBannerCTA, CourseBannerImg };
